import React, { useCallback, useMemo, useState } from 'react';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { useConversationSignalIncidents } from 'src/graphql/entities/signal-incident/hooks/use-conversation-signal-incidents';
import { useSignalIncidentLiveUpdates } from 'src/graphql/entities/signal-incident/hooks/use-signal-incident-live-updates';
import { useUpdateSignalIncident } from 'src/graphql/entities/signal-incident/hooks/use-update-signal-incident';
import type { SignalIncident } from 'src/graphql/entities/signal-incident/signal-incident.types';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import { SignalIncidentContext, SignalIncidentContextValue } from './signal-incident-context';

function useSignalIncidentSource(): {
  incidents: SignalIncident[];
  isLoading: boolean;
  error: Error | undefined;
} {
  const conversationId = useChatCommonData((s) => s.conversationId);
  const partnerId = useChatCommonData((s) => s.partnerId);
  const canView = useChatCommonData((s) =>
    Boolean(s.groupRules[UserGroupRules.AccessToViewSignalIncidentInConversation]),
  );

  const partnerIdNumber = useMemo(() => {
    const parsed = Number(partnerId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [partnerId]);

  // Keeps the card current as the notifier writes each notification, with no polling.
  useSignalIncidentLiveUpdates(canView ? conversationId : undefined);

  const { signalIncidents, loading, error } = useConversationSignalIncidents({
    conversationId,
    partnerId: partnerIdNumber,
    skip: !canView,
  });

  return { incidents: signalIncidents, isLoading: loading, error };
}

export const SignalIncidentProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const { updateSignalIncident } = useUpdateSignalIncident();
  const { incidents, isLoading, error } = useSignalIncidentSource();

  const resolve = useCallback(
    async (incident: SignalIncident) => {
      if (updatingId === incident.signalIncidentId) {
        return;
      }

      setUpdatingId(incident.signalIncidentId);

      try {
        await updateSignalIncident({ signalIncidentId: incident.signalIncidentId });
      } catch (resolveError) {
        console.error(resolveError);
      } finally {
        setUpdatingId((current) => (current === incident.signalIncidentId ? null : current));
      }
    },
    [updateSignalIncident, updatingId],
  );

  const value = useMemo<SignalIncidentContextValue>(
    () => ({ updatingId, resolve, incidents, isLoading, error }),
    [updatingId, resolve, incidents, isLoading, error],
  );

  return (
    <SignalIncidentContext.Provider value={value}>{children}</SignalIncidentContext.Provider>
  );
};
